import React, { useCallback, useEffect, useRef } from 'react';
import * as monaco from 'monaco-editor';
import Editor, { Monaco } from '@monaco-editor/react';
import { IQueryFieldsItem } from 'src/interfaces/entities/observer';
import { ICustomMessageEditorProps } from 'src/pages/indicators/EditIndicatorPage/_components/CustomEditor/types';
import { IDashboardTree } from 'src/pages/indicators/EditIndicatorPage/types/dashboardTree';

const generateKeyDocs = (
	sqlQuery?: IQueryFieldsItem[]
): { property: string; docs: monaco.IMarkdownString }[] => {
	return [
		{
			property: 'occurence',
			docs: {
				value: 'Objeto representando a ocorrência.',
			},
		},
		{
			property: 'observer',
			docs: {
				value: 'Objeto representando o observador.',
			},
		},
		{
			property: 'occurence.affectedRows',
			docs: {
				value: `Objeto representando as linhas afetadas.`,
			},
		},
		{
			property: 'occurence.affectedRows.result',
			docs: {
				value: `Array de objetos representando as linhas afetadas.
                Cada objeto contém os campos afetados.
                
                - Propriedades disponíveis:
                ${sqlQuery?.map((item) => item.field).join(', ')}`,
			},
		},
		{
			property: 'occurence.affectedRows.count',
			docs: {
				value: `Número de linhas afetadas.`,
			},
		},
		{
			property: 'occurence.affectedFields',
			docs: {
				value: `Objeto representando os campos afetados.`,
			},
		},
		{
			property: 'occurence.affectedFields.result',
			docs: {
				value: `Array de strings representando os campos afetados.
                  Exemplo:
                [${sqlQuery?.map((item) => item.field).join(', ')}]`,
			},
		},
		{
			property: 'occurence.affectedFields.count',
			docs: {
				value: `Número de campos afetados.`,
			},
		},
		{
			property: 'occurence.meta',
			docs: {
				value: `Objeto representando os metadados da ocorrência.`,
			},
		},
		{
			property: 'occurence.meta.executionMS',
			docs: {
				value: `Tempo de execução da query em milissegundos.`,
			},
		},
		{
			property: 'occurence.meta.totalRowsCount',
			docs: {
				value: `Número total de linhas da ocorrência.`,
			},
		},
		{
			property: 'occurence.meta.executionDate',
			docs: {
				value: `Data de execução da ocorrência.`,
			},
		},
		{
			property: 'observer.observerId',
			docs: {
				value: `ID do observador.`,
			},
		},
		{
			property: 'observer.name',
			docs: {
				value: `Nome do observador.`,
			},
		},
		{
			property: 'severity.name',
			docs: {
				value: `Nome da severidade da ocorrência.`,
			},
		},
	];
};

export const CustomEditor = ({
	sqlQueries,
	onChange,
	value,
	dashboardTree,
}: ICustomMessageEditorProps) => {
	const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
	const disposeSuggestionsRef = useRef<monaco.IDisposable>();

	useEffect(() => {
		return () => {
			disposeSuggestionsRef.current?.dispose();
		};
	}, []);
	const getSuggestions = useCallback(
		(object: any, path: string[]): monaco.languages.CompletionItem[] => {
			let properties = object;

			for (const key of path) {
				if (Array.isArray(properties)) {
					editorRef.current?.trigger(
						'keyboard',
						'editor.action.triggerSuggest',
						{}
					);
					properties = properties[0];
				} else if (properties && typeof properties === 'object') {
					properties = properties[key];
				} else {
					return [];
				}
			}

			const sqlQuery = sqlQueries[path[0]];
			if (properties && typeof properties === 'object') {
				return Object.keys(properties).map(
					//@ts-ignore
					(key): monaco.languages.CompletionItem => ({
						label: key,
						insertText: key,
						kind: monaco.languages.CompletionItemKind.Property,
						detail: 'Detalhes',
						documentation: generateKeyDocs(sqlQuery).find(
							(doc) =>
								doc.property ===
                                path?.slice(1).join('.') + '.' + key
						)?.docs,
					})
				);
			}

			return [];
		},
		[]
	);

	const registerCompletionProvider = (
		object: IDashboardTree,
		monaco: Monaco
	) => {
		return monaco.languages.registerCompletionItemProvider('twig', {
			triggerCharacters: [ '.', ],
			provideCompletionItems: (model, position) => {
				let suggestions: monaco.languages.CompletionItem[] = [];

				const wordInfo = model.getWordUntilPosition({
					lineNumber: position.lineNumber,
					column: position.column - 1,
				});
				const path = wordInfo.word.split('.').filter(Boolean);
				const fullPathRange = model.findPreviousMatch(
					/([a-zA-Z+]+\.)+/g as unknown as string,
					position,
					true,
					false,
					null,
					true
				)?.range;
				const fullPath = fullPathRange
					? model.getValueInRange(fullPathRange)
					: '';
				const fullPathParts = fullPath.split('.').filter(Boolean);

				if (path.length > 0) {
					try {
						suggestions = getSuggestions(object, fullPathParts);
					} catch (error) {
						console.error(
							'Invalid path for suggestions:',
							path,
							error
						);
					}
				}

				if (suggestions.length === 0) {
					//@ts-ignore
					suggestions.push({
						label: 'text',
						insertText:
                            'text(${1: texto: string}, ${2: corDeFundo: string}, ${3: corDotexto: string}, ${4: pisca: boolean})',
						kind: monaco.languages.CompletionItemKind.Variable,
						insertTextRules:
                            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
						detail: 'Detalhes',
						documentation: generateKeyDocs().find(
							(doc) => doc.property === 'occurence'
						)?.docs,
					});

					Object.keys(sqlQueries).forEach((key) => {
						//@ts-ignore
						suggestions.push({
							label: key,
							insertText: key,
							kind: monaco.languages.CompletionItemKind.Variable,
							detail: 'Detalhes',
							documentation: 'Observador',
						});
					});
				}

				return { suggestions: suggestions, };
			},
		});
	};

	const beforeMount = (monaco: Monaco) => {
		//sees if there are already suggestions

		const disposeSuggestions = registerCompletionProvider(
			dashboardTree,
			monaco
		);

		disposeSuggestionsRef.current = disposeSuggestions;
	};

	return (
		<Editor
			value={value}
			onChange={(val) => onChange(val ?? '')}
			language='twig'
			beforeMount={beforeMount}
			options={{
				minimap: {
					enabled: false,
				},
			}}
		/>
	);
};